import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import cytoscape, { Core, EventObject } from 'cytoscape';
import { X, Globe } from 'lucide-react';

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#eab308',
  low: '#3b82f6',
  info: '#6b7280',
};

const NODE_STYLES: Record<string, { color: string; size: number; borderColor: string }> = {
  target:     { color: '#7c3aed', size: 60, borderColor: '#6d28d9' },
  subdomain:  { color: '#3b82f6', size: 35, borderColor: '#2563eb' },
  technology: { color: '#10b981', size: 30, borderColor: '#059669' },
  vuln:       { color: '#ea580c', size: 28, borderColor: '#c2410c' },
  breach:     { color: '#dc2626', size: 32, borderColor: '#991b1b' },
};

const SCAN_TYPE_TO_ASSET: Record<string, string> = {
  subdomain: 'subdomain',
  subfinder: 'subdomain',
  surface_discovery: 'subdomain',
  tech: 'technology',
  ssl: 'technology',
  dns: 'technology',
  headers: 'technology',
  ports: 'technology',
  nmap: 'technology',
  sqli_test: 'vuln',
  xss_test: 'vuln',
  brute_login: 'vuln',
  dir_traversal: 'vuln',
  csrf_active: 'vuln',
  clickjack_poc: 'vuln',
  session_analysis: 'vuln',
  rate_limit_test: 'vuln',
  user_enum: 'vuln',
  js_analysis: 'vuln',
  info_disclosure: 'vuln',
  admin_panels: 'vuln',
  tenant_enum: 'vuln',
  darkweb_intel: 'breach',
};

function extractAssetLabel(finding: Record<string, unknown>): string {
  const title = (finding.title as string) || '';
  const scanType = (finding.scan_type as string) || '';

  if (scanType === 'subdomain' || scanType === 'subfinder' || scanType === 'surface_discovery') {
    const match = title.match(/([a-zA-Z0-9][-a-zA-Z0-9]*\.[a-zA-Z0-9][-a-zA-Z0-9.]+)/);
    if (match) return match[1];
  }

  if (scanType === 'tech') {
    const match = title.match(/^(.+?)(?:\s+detected|\s+found|\s+v[\d.]|$)/i);
    if (match) return match[1].substring(0, 30);
  }

  return title.length > 30 ? title.substring(0, 27) + '...' : title;
}

function getRingLevel(assetType: string): number {
  switch (assetType) {
    case 'subdomain': return 1;
    case 'technology': return 2;
    case 'vuln': return 3;
    case 'breach': return 4;
    default: return 3;
  }
}

interface AssetGraphProps {
  findings: Record<string, unknown>[];
  target: string;
}

export default function AssetGraph({ findings, target }: AssetGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);
  const [selected, setSelected] = useState<Record<string, unknown> | null>(null);

  const { elements, assetCounts } = useMemo(() => {
    const nodes: cytoscape.ElementDefinition[] = [];
    const edges: cytoscape.ElementDefinition[] = [];
    const seenLabels = new Set<string>();
    const counts = { subdomains: 0, technologies: 0, vulns: 0, breaches: 0 };

    nodes.push({
      data: {
        id: 'center-target',
        label: target,
        assetType: 'target',
        ring: 0,
      },
    });

    const subdomainNodes = new Map<string, string>();
    const techNodes = new Map<string, string>();

    for (let i = 0; i < findings.length; i++) {
      const f = findings[i];
      const scanType = (f.scan_type as string) || '';
      const assetType = SCAN_TYPE_TO_ASSET[scanType] || 'vuln';
      const severity = (f.severity as string) || 'info';
      const label = extractAssetLabel(f);
      const ring = getRingLevel(assetType);

      const dedupeKey = `${assetType}-${label}`;
      if (seenLabels.has(dedupeKey)) continue;
      seenLabels.add(dedupeKey);

      const nodeId = `asset-${i}`;

      nodes.push({
        data: {
          id: nodeId,
          label,
          assetType,
          severity,
          ring,
          scanType,
          finding: f,
        },
      });

      if (assetType === 'subdomain') {
        counts.subdomains++;
        subdomainNodes.set(label, nodeId);
        edges.push({
          data: {
            id: `edge-center-${nodeId}`,
            source: 'center-target',
            target: nodeId,
            edgeType: 'owns',
          },
        });
      } else if (assetType === 'technology') {
        counts.technologies++;
        techNodes.set(label, nodeId);
        edges.push({
          data: {
            id: `edge-center-${nodeId}`,
            source: 'center-target',
            target: nodeId,
            edgeType: 'runs',
          },
        });
      } else if (assetType === 'vuln') {
        counts.vulns++;
        let parentFound = false;
        if (techNodes.size > 0) {
          const parentId = Array.from(techNodes.values())[
            i % techNodes.size
          ];
          edges.push({
            data: {
              id: `edge-${parentId}-${nodeId}`,
              source: parentId,
              target: nodeId,
              edgeType: 'exposes',
            },
          });
          parentFound = true;
        }
        if (!parentFound) {
          edges.push({
            data: {
              id: `edge-center-${nodeId}`,
              source: 'center-target',
              target: nodeId,
              edgeType: 'exposes',
            },
          });
        }
      } else if (assetType === 'breach') {
        counts.breaches++;
        edges.push({
          data: {
            id: `edge-center-${nodeId}`,
            source: 'center-target',
            target: nodeId,
            edgeType: 'leaked',
          },
        });
      }
    }

    return {
      elements: [...nodes, ...edges],
      assetCounts: counts,
    };
  }, [findings, target]);

  const handleNodeTap = useCallback((evt: EventObject) => {
    const node = evt.target;
    const data = node.data();
    if (data.finding) {
      setSelected(data.finding as Record<string, unknown>);
    } else if (data.assetType === 'target') {
      setSelected({ title: target, description: 'Target domain - root of the attack surface', severity: 'info' });
    }
  }, [target]);

  useEffect(() => {
    if (!containerRef.current) return;

    const cy = cytoscape({
      container: containerRef.current,
      elements,
      style: [
        {
          selector: 'node[assetType="target"]',
          style: {
            'label': 'data(label)',
            'text-valign': 'center',
            'text-halign': 'center',
            'font-size': '10px',
            'font-weight': 'bold',
            'color': '#ffffff',
            'text-wrap': 'wrap',
            'text-max-width': '80px',
            'background-color': NODE_STYLES.target.color,
            'border-width': 3,
            'border-color': NODE_STYLES.target.borderColor,
            'width': NODE_STYLES.target.size,
            'height': NODE_STYLES.target.size,
            'shape': 'ellipse',
          } as unknown as cytoscape.Css.Node,
        },
        {
          selector: 'node[assetType="subdomain"]',
          style: {
            'label': 'data(label)',
            'text-valign': 'bottom',
            'text-halign': 'center',
            'font-size': '7px',
            'color': '#93c5fd',
            'text-margin-y': 4,
            'text-wrap': 'wrap',
            'text-max-width': '90px',
            'background-color': NODE_STYLES.subdomain.color,
            'border-width': 2,
            'border-color': NODE_STYLES.subdomain.borderColor,
            'width': NODE_STYLES.subdomain.size,
            'height': NODE_STYLES.subdomain.size,
            'shape': 'ellipse',
          } as unknown as cytoscape.Css.Node,
        },
        {
          selector: 'node[assetType="technology"]',
          style: {
            'label': 'data(label)',
            'text-valign': 'bottom',
            'text-halign': 'center',
            'font-size': '7px',
            'color': '#6ee7b7',
            'text-margin-y': 4,
            'text-wrap': 'wrap',
            'text-max-width': '80px',
            'background-color': NODE_STYLES.technology.color,
            'border-width': 2,
            'border-color': NODE_STYLES.technology.borderColor,
            'width': NODE_STYLES.technology.size,
            'height': NODE_STYLES.technology.size,
            'shape': 'diamond',
          } as unknown as cytoscape.Css.Node,
        },
        {
          selector: 'node[assetType="vuln"]',
          style: {
            'label': 'data(label)',
            'text-valign': 'bottom',
            'text-halign': 'center',
            'font-size': '6px',
            'color': '#cbd5e1',
            'text-margin-y': 3,
            'text-wrap': 'wrap',
            'text-max-width': '70px',
            'width': NODE_STYLES.vuln.size,
            'height': NODE_STYLES.vuln.size,
            'shape': 'hexagon',
            'border-width': 2,
          } as unknown as cytoscape.Css.Node,
        },
        {
          selector: 'node[assetType="vuln"][severity="critical"]',
          style: { 'background-color': '#dc2626', 'border-color': '#b91c1c' },
        },
        {
          selector: 'node[assetType="vuln"][severity="high"]',
          style: { 'background-color': '#ea580c', 'border-color': '#c2410c' },
        },
        {
          selector: 'node[assetType="vuln"][severity="medium"]',
          style: { 'background-color': '#eab308', 'border-color': '#ca8a04' },
        },
        {
          selector: 'node[assetType="vuln"][severity="low"]',
          style: { 'background-color': '#3b82f6', 'border-color': '#2563eb' },
        },
        {
          selector: 'node[assetType="vuln"][severity="info"]',
          style: { 'background-color': '#6b7280', 'border-color': '#4b5563' },
        },
        {
          selector: 'node[assetType="breach"]',
          style: {
            'label': 'data(label)',
            'text-valign': 'bottom',
            'text-halign': 'center',
            'font-size': '7px',
            'color': '#fca5a5',
            'text-margin-y': 4,
            'text-wrap': 'wrap',
            'text-max-width': '80px',
            'background-color': NODE_STYLES.breach.color,
            'border-width': 2,
            'border-color': NODE_STYLES.breach.borderColor,
            'width': NODE_STYLES.breach.size,
            'height': NODE_STYLES.breach.size,
            'shape': 'star',
          } as unknown as cytoscape.Css.Node,
        },
        {
          selector: 'edge[edgeType="owns"]',
          style: {
            'width': 2,
            'line-color': '#334155',
            'target-arrow-color': '#334155',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            'arrow-scale': 0.7,
          } as unknown as cytoscape.Css.Edge,
        },
        {
          selector: 'edge[edgeType="runs"]',
          style: {
            'width': 1.5,
            'line-color': '#1e3a2f',
            'target-arrow-color': '#1e3a2f',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            'arrow-scale': 0.6,
            'line-style': 'dashed',
          } as unknown as cytoscape.Css.Edge,
        },
        {
          selector: 'edge[edgeType="exposes"]',
          style: {
            'width': 1,
            'line-color': '#78350f',
            'target-arrow-color': '#78350f',
            'target-arrow-shape': 'vee',
            'curve-style': 'bezier',
            'arrow-scale': 0.5,
            'opacity': 0.6,
          } as unknown as cytoscape.Css.Edge,
        },
        {
          selector: 'edge[edgeType="leaked"]',
          style: {
            'width': 2,
            'line-color': '#7f1d1d',
            'target-arrow-color': '#7f1d1d',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            'arrow-scale': 0.7,
            'line-style': 'dotted',
          } as unknown as cytoscape.Css.Edge,
        },
      ],
      layout: {
        name: 'concentric',
        concentric: (node: cytoscape.NodeSingular) => {
          const ring = node.data('ring') as number;
          return 4 - ring;
        },
        levelWidth: () => 1,
        minNodeSpacing: 40,
        padding: 30,
        startAngle: (3 / 2) * Math.PI,
        clockwise: true,
        avoidOverlap: true,
      } as unknown as cytoscape.LayoutOptions,
      userZoomingEnabled: true,
      userPanningEnabled: true,
      boxSelectionEnabled: false,
    });

    cy.on('tap', 'node', handleNodeTap);
    cyRef.current = cy;

    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, [elements, handleNodeTap]);

  const totalAssets = assetCounts.subdomains + assetCounts.technologies + assetCounts.vulns + assetCounts.breaches;
  if (totalAssets === 0) return null;

  return (
    <div className="card !p-0 overflow-hidden print:hidden relative" style={{ height: '480px' }}>
      <div className="px-4 py-3 bg-gradient-to-r from-gray-900 to-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-purple-400" />
          <h3 className="text-sm font-bold text-white">Superficie de Ataque</h3>
          <span className="text-[10px] bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full font-medium">
            {totalAssets} activos
          </span>
        </div>
        <div className="flex items-center gap-3 text-[9px]">
          {assetCounts.subdomains > 0 && (
            <span className="flex items-center gap-1">
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: NODE_STYLES.subdomain.color, display: 'inline-block' }} />
              <span style={{ color: '#9ca3af' }}>{assetCounts.subdomains} subdominios</span>
            </span>
          )}
          {assetCounts.technologies > 0 && (
            <span className="flex items-center gap-1">
              <span style={{ width: 8, height: 8, background: NODE_STYLES.technology.color, display: 'inline-block', transform: 'rotate(45deg)' }} />
              <span style={{ color: '#9ca3af' }}>{assetCounts.technologies} tecnologias</span>
            </span>
          )}
          {assetCounts.vulns > 0 && (
            <span className="flex items-center gap-1">
              <span style={{ width: 8, height: 8, background: '#ea580c', display: 'inline-block', clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }} />
              <span style={{ color: '#9ca3af' }}>{assetCounts.vulns} vulns</span>
            </span>
          )}
          {assetCounts.breaches > 0 && (
            <span className="flex items-center gap-1">
              <span style={{ width: 10, height: 10, background: NODE_STYLES.breach.color, display: 'inline-block', clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
              <span style={{ color: '#9ca3af' }}>{assetCounts.breaches} breaches</span>
            </span>
          )}
        </div>
      </div>

      <div ref={containerRef} style={{ width: '100%', height: 'calc(100% - 44px)', background: '#0f172a' }} />

      {selected && (
        <div className="absolute top-14 right-4 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[380px] overflow-y-auto">
          <div className="p-3">
            <div className="flex items-center justify-between mb-2">
              <span
                className="px-2 py-0.5 rounded text-[10px] font-bold text-white"
                style={{ background: SEVERITY_COLORS[(selected.severity as string)] || '#6b7280' }}
              >
                {((selected.severity as string) || '').toUpperCase()}
              </span>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <h4 className="font-bold text-sm text-gray-900">{selected.title as string}</h4>
            {typeof selected.scan_type === 'string' && selected.scan_type && (
              <p className="text-[10px] text-gray-500 mt-0.5">{selected.scan_type}</p>
            )}
            {typeof selected.description === 'string' && selected.description && (
              <p className="text-xs text-gray-600 mt-2">{selected.description.substring(0, 200)}</p>
            )}
            {typeof selected.evidence === 'string' && selected.evidence && (
              <div className="mt-2 p-2 bg-gray-50 rounded text-[10px] font-mono text-gray-600 break-all">
                {selected.evidence.substring(0, 150)}
              </div>
            )}
            {typeof selected.remediation === 'string' && selected.remediation && (
              <div className="mt-2 p-2 bg-emerald-50 rounded text-[10px] text-emerald-700">
                <strong>Fix:</strong> {selected.remediation.substring(0, 150)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
